import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";

import { Box, Button, Stack } from "@mui/material";
import { FormControlLabel, Switch } from "@mui/material";

import ConfirmationButton from "../components/shared/ConfirmationButton";
import { JsonTreeView, RawJsonView } from "../components/shared/JsonViewer";
import PageContainer from "../components/shared/PageContainer";
import { useAlert } from "../contexts/AlertContext";
import { Templates } from "../services/api";
import { TranscoderTemplate } from "../services/api/TranscoderTemplate";
import { nullValuesReplacer } from "../services/utils/json";

const TemplateDetails: React.FC = () => {
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { name } = useParams<{ name: string }>();
  const [treeView, setTreeView] = useState(true);
  const [encodingProfile, setEncodingProfile] = useState<{ [key: string]: any }>({});
  const [showNullValues, setShowNullValues] = useState(false);

  useEffect(() => {
    TranscoderTemplate.retrieve(name!).then((template) => {
      setEncodingProfile(template);
    });
  }, [name]);

  const copyJSONToClipboard = (data: any) => {
    navigator.clipboard.writeText(JSON.stringify(data, nullValuesReplacer(showNullValues), 2)).then(
      () => alert.success("JSON copied to clipboard!"),
      (err) => alert.error("Failed to copy to clipboard: " + err),
    );
  };

  const toggleNullValues = () => setShowNullValues((prev) => !prev);

  const deleteTemplate = async () => {
    await Templates.delete(name!, true);
    await TranscoderTemplate.delete(name!);

    navigate("/templates");
  };

  return (
    <PageContainer>
      <Stack
        direction="row"
        sx={{ padding: "3px" }}
        alignItems="center"
        justifyContent="flex-end"
        spacing={1}
      >
        <FormControlLabel
          control={<Switch checked={showNullValues} onChange={toggleNullValues} name="nullValuesSwitch" />}
          label="Null values"
        />
        <Button size="small" variant="contained" onClick={() => copyJSONToClipboard(encodingProfile)}>
          Copy JSON
        </Button>
        <Button size="small" variant="contained" onClick={() => setTreeView(!treeView)}>
          {treeView ? "Show Raw JSON" : "Show Tree View"}
        </Button>
        <ConfirmationButton
          sx={{ marginTop: "10pt" }}
          onConfirm={deleteTemplate}
          size="small"
          variant="contained"
          dialogTitle="Confirm Deletion"
          dialogMessage={`Deleting the job template / mapping is permanent and cannot be undone. Are you sure you want to delete "${name!}"?`}
          confirmInput={{
            confirmInputMatch: name!,
            confirmInputLabel: "Please confirm by typing the full name of the template below:",
          }}
        >
          Delete
        </ConfirmationButton>
      </Stack>

      <Box display="flex" justifyContent="center" alignItems="center" width="100%">
        {treeView ? (
          <JsonTreeView data={encodingProfile} title="Encoding Profile" showNullValues={showNullValues} />
        ) : (
          <RawJsonView data={encodingProfile} showNullValues={showNullValues} />
        )}
      </Box>
    </PageContainer>
  );
};

export default TemplateDetails;
